// React Query hooks for the skip-customers surface.
//
// Skip customers are a thin wrapper over /api/v1/skip/customers — the
// underlying rows live in the unified `customers` table, scoped to
// branch='skip'. The detail page links back to /customers/:id/edit
// for editing rather than duplicating the customer form.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type {
  CustomerListResponse,
  CustomerSummary,
  CreateCustomerInput,
} from '../../../types/api';

interface SkipCustomersFilters {
  page?: number;
  limit?: number;
  search?: string;
  /** Feature 1 — when true, the wrapper drops the implicit
   *  branch='skip' filter and returns every tenant customer.
   *  Default false (legacy behaviour). */
  show_all?: '1' | '0';
}

type SkipCustomerListResponse = CustomerListResponse;

export function useSkipCustomers(filters: SkipCustomersFilters = {}) {
  return useQuery<SkipCustomerListResponse>({
    queryKey: queryKeys.skipCustomers.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<SkipCustomerListResponse>(endpoints.skipCustomers.list, {
        params: filters,
      });
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useSkipCustomer(encId: string | null | undefined) {
  return useQuery<{ customer: CustomerSummary }>({
    queryKey: queryKeys.skipCustomers.detail(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipCustomer called without enc_id');
      const res = await apiClient.get<{ customer: CustomerSummary }>(
        endpoints.skipCustomers.detail(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

/**
 * POST /api/v1/skip/customers — Feature 1.
 * The skip wrapper auto-includes branch='skip' so the new customer
 * appears in the default skip-customers filter from the moment it is
 * created (no separate branch-add round-trip needed).
 */
export function useCreateSkipCustomer() {
  const qc = useQueryClient();
  return useMutation<{ customer: CustomerSummary & { enc_id: string } }, Error, CreateCustomerInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { customer: CustomerSummary & { enc_id: string } },
        CreateCustomerInput
      >(endpoints.skipCustomers.create, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipCustomers.all });
    },
  });
}

export function useCheckCustomerDuplicates() {
  return useMutation<
    { matches: CustomerSummary[] },
    Error,
    { name: string; phone?: string | null; postcode?: string | null }
  >({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ matches: CustomerSummary[] }>(
        endpoints.skipCustomers.checkDuplicates,
        body,
      );
      return res.data;
    },
  });
}
